import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Steps,
  Alert,
  Space,
  Typography,
  Table,
  Tag,
  Progress,
  Result,
  Button,
  Upload,
  Tooltip,
  Row,
  Col,
  message,
  notification,
} from 'antd';
import * as XLSX from 'xlsx';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import type { ColumnsType } from 'antd/es/table';
import apiService from '../../../services/apiService';

const { Title, Text, Paragraph } = Typography;

interface ValidationError {
  row: number;
  field: string;
  value: any;
  error: string;
  severity: 'error' | 'warning';
}

interface PreviewItem {
  row: number;
  orderNumber?: string;
  customer: string;
  customerName: string;
  warehouse: string;
  warehouseName: string;
  productSku: string;
  expectedQuantity: number;
  baseUnit: string;
  packagingUnit: string;
  expectedArrivalDate: string;
  expiryDate?: string;
  logisticsMethod?: string;
  trackingNumber?: string;
  shipperContactName?: string;
  shipperContactPhone?: string;
  cargoNotes?: string;
  status: 'valid' | 'warning' | 'error';
  errors: ValidationError[];
}

const InboundBulkUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewData, setPreviewData] = useState<PreviewItem[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [uploadedFile, setUploadedFile] = useState<any>(null);

  // 字段說明（必填在前、可選在後）
  const fieldDescriptions = [
    // 必填字段（與入庫訂單表單要求一致）
    { field: 'customerName', name: '客戶名稱', required: true, description: '客戶的完整名稱' },
    { field: 'expectedArrivalDate', name: '預期到貨日期', required: true, description: '預計貨物到達日期（YYYY-MM-DD格式）' },
    { field: 'productSku', name: 'SKU ID', required: true, description: '產品SKU編碼' },
    { field: 'expectedQuantity', name: '預期數量', required: true, description: '預期入庫的數量' },
    // 可選字段
    { field: 'warehouseName', name: '倉庫名稱', required: false, description: '倉庫的完整名稱' },
    { field: 'logisticsMethod', name: '物流方式', required: false, description: '運輸方式（如：DHL、順豐速運）' },
    { field: 'shipperContactName', name: '發貨聯繫人', required: false, description: '發貨方聯繫人姓名' },
    { field: 'shipperContactPhone', name: '發貨聯繫電話', required: false, description: '發貨方聯繫電話' },
    { field: 'baseUnit', name: '基本單位', required: false, description: '計量的基本單位（如：PCS、KG）' },
    // 可選字段
    { field: 'orderNumber', name: '訂單號', required: false, description: '入庫訂單編號（系統自動生成，Excel中的訂單號會被忽略）' },
    { field: 'customer', name: '客戶', required: false, description: '客戶的識別代碼或編號' },
    { field: 'warehouse', name: '倉庫', required: false, description: '倉庫的識別代碼' },
    { field: 'packagingUnit', name: '包裝單位', required: false, description: '包裝的單位（如：BOX、CASE）' },
    { field: 'expiryDate', name: '到期日期', required: false, description: '產品到期日期（YYYY-MM-DD格式）' },
    { field: 'trackingNumber', name: '追蹤號碼', required: false, description: '物流追蹤號碼' },
    { field: 'cargoNotes', name: '貨物備註', required: false, description: '關於貨物的額外說明或備註' },
    { field: 'lotNumber', name: '批次號', required: false, description: '產品批次號碼' },
    { field: 'serialNumber', name: '序號', required: false, description: '產品序列號' },
    { field: 'currentLocation', name: '貨位', required: false, description: '存放貨位位置' },
    { field: 'length', name: '長度', required: false, description: '產品長度（cm）' },
    { field: 'width', name: '寬度', required: false, description: '產品寬度（cm）' },
    { field: 'height', name: '高度', required: false, description: '產品高度（cm）' },
    { field: 'weight', name: '重量', required: false, description: '產品重量（kg）' },
    { field: 'remark', name: '明細備註', required: false, description: '產品明細備註' }
  ];

  // 下載模板 - 展示多產品訂單功能與空字段繼承（包含所有字段）- Excel格式
  const downloadTemplate = () => {
    const required = fieldDescriptions.filter(f => f.required);
    const optional = fieldDescriptions.filter(f => !f.required);
    const allFields = [...required, ...optional];

    // 構建示例數據（訂單 IN-2025-001：3個產品，訂單 IN-2025-002：2個產品）
    const example1: Record<string, string> = {
      customerName: '深圳科技有限公司', productSku: 'SKU001', orderNumber: 'IN-2025-001',
      customer: 'CUST001', warehouse: 'WH001', warehouseName: '主倉庫',
      expectedQuantity: '100', baseUnit: 'PCS', packagingUnit: 'BOX',
      expectedArrivalDate: '2025-01-15', expiryDate: '2026-01-15',
      logisticsMethod: 'DHL', trackingNumber: 'DHL1234567890',
      shipperContactName: '張先生', shipperContactPhone: '13800000001',
      cargoNotes: '電子產品批次', lotNumber: 'LOT20250115', serialNumber: '',
      currentLocation: 'A-01-01', length: '30', width: '20', height: '15', weight: '5.5',
      remark: '易碎品'
    };

    const example2: Record<string, string> = {
      customerName: '', productSku: 'SKU002', orderNumber: '',
      customer: '', warehouse: '', warehouseName: '',
      expectedQuantity: '200', baseUnit: '', packagingUnit: '',
      expectedArrivalDate: '', expiryDate: '2026-01-16',
      logisticsMethod: '', trackingNumber: '',
      shipperContactName: '', shipperContactPhone: '',
      cargoNotes: '', lotNumber: 'LOT20250115', serialNumber: '',
      currentLocation: 'A-01-02', length: '40', width: '25', height: '20', weight: '8.0',
      remark: '防潮'
    };

    const example3: Record<string, string> = {
      customerName: '', productSku: 'SKU003', orderNumber: '',
      customer: '', warehouse: '', warehouseName: '',
      expectedQuantity: '150', baseUnit: '', packagingUnit: '',
      expectedArrivalDate: '', expiryDate: '2026-01-17',
      logisticsMethod: '', trackingNumber: '',
      shipperContactName: '', shipperContactPhone: '',
      cargoNotes: '', lotNumber: 'LOT20250115', serialNumber: '',
      currentLocation: 'A-01-03', length: '35', width: '22', height: '18', weight: '6.8',
      remark: '常溫存放'
    };

    const example4: Record<string, string> = {
      customerName: '上海貿易公司', productSku: 'SKU004', orderNumber: 'IN-2025-002',
      customer: 'CUST002', warehouse: 'WH001', warehouseName: '主倉庫',
      expectedQuantity: '50', baseUnit: 'BOX', packagingUnit: 'PALLET',
      expectedArrivalDate: '2025-01-16', expiryDate: '2026-02-01',
      logisticsMethod: '順豐速運', trackingNumber: 'SF9876543210',
      shipperContactName: '李女士', shipperContactPhone: '13900000002',
      cargoNotes: '機械配件', lotNumber: 'LOT20250116', serialNumber: '',
      currentLocation: 'B-02-01', length: '50', width: '30', height: '25', weight: '12.5',
      remark: '重物小心'
    };

    const example5: Record<string, string> = {
      customerName: '', productSku: 'SKU005', orderNumber: '',
      customer: '', warehouse: '', warehouseName: '',
      expectedQuantity: '80', baseUnit: '', packagingUnit: '',
      expectedArrivalDate: '', expiryDate: '2026-02-15',
      logisticsMethod: '', trackingNumber: '',
      shipperContactName: '', shipperContactPhone: '',
      cargoNotes: '', lotNumber: 'LOT20250116', serialNumber: '',
      currentLocation: 'B-02-02', length: '45', width: '28', height: '22', weight: '10.0',
      remark: '防塵'
    };

    // 構建Excel數據
    const headers = allFields.map(f => f.name);
    const data = [
      headers,
      allFields.map(f => example1[f.field] ?? ''),
      allFields.map(f => example2[f.field] ?? ''),
      allFields.map(f => example3[f.field] ?? ''),
      allFields.map(f => example4[f.field] ?? ''),
      allFields.map(f => example5[f.field] ?? '')
    ];

    // 創建工作簿和工作表
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '入庫訂單模板');

    // 設置列寬
    const colWidths = allFields.map(() => ({ wch: 15 }));
    worksheet['!cols'] = colWidths;

    // 下載Excel文件
    XLSX.writeFile(workbook, '入庫訂單模板.xlsx');
    message.success('Excel模板文件下載成功（包含所有字段與空字段繼承示例）');
  };

  // 上傳配置
  const uploadProps = {
    accept: '.csv,.xlsx,.xls',
    showUploadList: false,
    beforeUpload: (file: UploadFile) => {
      setUploadedFile(file);
      processFile(file);
      return false;
    },
  };

  // 處理文件
  const processFile = async (file: UploadFile) => {
    setIsProcessing(true);
    setUploadProgress(0);

    try {
      const fileName = (file as any).name || '';
      const isExcelFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
      
      let parsedData: any[];
      
      if (isExcelFile) {
        // Excel 文件處理
        console.log('檢測到 Excel 文件，使用 XLSX 庫解析');
        const fileContent = await readFileAsArrayBuffer(file);
        setUploadProgress(30);
        parsedData = parseExcelContent(fileContent);
        setUploadProgress(60);
      } else {
        // CSV 文件處理
        console.log('檢測到 CSV 文件，使用文本解析');
        const fileContent = await readFileContent(file);
        setUploadProgress(30);
        parsedData = parseCSVContent(fileContent);
        setUploadProgress(60);
      }

      // 驗證數據
      const validatedData = validateParsedData(parsedData);
      setUploadProgress(90);

      // 生成驗證錯誤列表
      const errors = generateValidationErrors(validatedData);
      
      setPreviewData(validatedData);
      setValidationErrors(errors);
      setUploadProgress(100);
      
      setIsProcessing(false);
      setCurrentStep(1);
    } catch (error) {
      console.error('文件處理錯誤:', error);
      const errorMessage = error instanceof Error ? error.message : '未知錯誤';
      
      // 提供更詳細的錯誤提示
      if (errorMessage.includes('格式不正確')) {
        message.error({
          content: '文件格式錯誤！請確保：1) 文件包含標題行和數據行；2) 如果用Excel編輯過，請另存為CSV (UTF-8) 格式或直接上傳 .xlsx 文件',
          duration: 6
        });
      } else {
        message.error(`文件處理失敗：${errorMessage}`);
      }
      
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  // 讀取文件內容（文本格式 - 用於 CSV）
  const readFileContent = (file: UploadFile): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        resolve(content);
      };
      reader.onerror = () => reject(new Error('文件讀取失敗'));
      reader.readAsText(file as unknown as File, 'UTF-8');
    });
  };

  // 讀取文件內容（二進制格式 - 用於 Excel）
  const readFileAsArrayBuffer = (file: UploadFile): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as ArrayBuffer;
        resolve(content);
      };
      reader.onerror = () => reject(new Error('文件讀取失敗'));
      reader.readAsArrayBuffer(file as unknown as File);
    });
  };

  // 將 Excel 日期序列號轉換為 YYYY-MM-DD 格式
  const convertExcelDate = (value: any): string => {
    if (!value) return '';
    
    // 如果已經是 YYYY-MM-DD 格式，直接返回
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    
    // 如果是數字（Excel 日期序列號）
    if (typeof value === 'number') {
      // Excel 日期從 1900-01-01 開始計算（序列號 1）
      // JavaScript Date 從 1970-01-01 開始
      const excelEpoch = new Date(1899, 11, 30); // Excel 的基準日期
      const date = new Date(excelEpoch.getTime() + value * 86400000);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    }
    
    // 如果是 Date 對象
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    }
    
    // 嘗試解析其他日期格式（如 15/1/2025、2025/1/15、15-1-2025 等）
    const str = String(value).trim();
    
    // 匹配各種日期格式
    const patterns = [
      /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/,  // DD/MM/YYYY 或 MM/DD/YYYY
      /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/,  // YYYY/MM/DD
    ];
    
    for (const pattern of patterns) {
      const match = str.match(pattern);
      if (match) {
        let year, month, day;
        
        if (match[1].length === 4) {
          // YYYY/MM/DD 格式
          year = match[1];
          month = match[2].padStart(2, '0');
          day = match[3].padStart(2, '0');
        } else {
          // DD/MM/YYYY 格式（假設是日/月/年）
          day = match[1].padStart(2, '0');
          month = match[2].padStart(2, '0');
          year = match[3];
        }
        
        return `${year}-${month}-${day}`;
      }
    }
    
    // 如果無法解析，返回原值
    return str;
  };

  // 解析 Excel 內容
  const parseExcelContent = (arrayBuffer: ArrayBuffer): any[] => {
    try {
      // 讀取 Excel 文件（保留原始格式）
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
      
      // 獲取第一個工作表
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // 轉換為 JSON 格式（保留標題行）
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: false }) as any[][];
      
      if (jsonData.length < 2) {
        throw new Error('Excel 文件格式不正確，至少需要標題行和一行數據');
      }
      
      // 第一行是標題
      const headers = jsonData[0].map((h: any) => String(h || '').trim());
      
      // 識別日期字段（根據字段名稱）
      const dateFields = ['expectedArrivalDate', 'expiryDate'];
      const dateFieldIndices = headers.map((header, index) => {
        const fieldKey = getFieldKey(header);
        return dateFields.includes(fieldKey) ? index : -1;
      }).filter(index => index !== -1);
      
      // 後續行是數據
      const data: any[] = [];
      for (let i = 1; i < jsonData.length; i++) {
        const row: any = { row: i };
        const values = jsonData[i];
        
        headers.forEach((header, index) => {
          const fieldKey = getFieldKey(header);
          let value = values[index];
          
          // 如果是日期字段，進行格式轉換
          if (dateFieldIndices.includes(index)) {
            value = convertExcelDate(value);
          } else {
            value = String(value || '').trim();
          }
          
          row[fieldKey] = value;
        });
        
        data.push(row);
      }
      
      console.log(`成功解析 Excel 文件：${headers.length} 個欄位，${data.length} 行數據`);
      return data;
    } catch (error) {
      console.error('Excel 解析錯誤:', error);
      throw new Error('Excel 文件解析失敗：' + (error instanceof Error ? error.message : '未知錯誤'));
    }
  };

  // 自動檢測CSV分隔符
  const detectDelimiter = (content: string): string => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) return ',';
    
    const firstLine = lines[0];
    const delimiters = [',', ';', '\t', '|'];
    const counts = delimiters.map(delim => ({
      delimiter: delim,
      count: firstLine.split(delim).length
    }));
    
    // 選擇分隔最多字段的分隔符
    counts.sort((a, b) => b.count - a.count);
    return counts[0].count > 1 ? counts[0].delimiter : ',';
  };

  // 解析CSV內容
  const parseCSVContent = (content: string): any[] => {
    // 移除BOM字符
    content = content.replace(/^\uFEFF/, '');
    
    const lines = content.split('\n').filter(line => line.trim());
    
    if (lines.length < 2) {
      throw new Error('CSV文件格式不正確，至少需要標題行和一行數據');
    }

    // 自動檢測分隔符
    const delimiter = detectDelimiter(content);
    console.log('檢測到的CSV分隔符:', delimiter === ',' ? '逗號' : delimiter === ';' ? '分號' : delimiter === '\t' ? '制表符' : delimiter);

    const headers = lines[0].split(delimiter).map(header => header.trim().replace(/"/g, '').replace(/\r/g, ''));
    
    // 識別日期字段（根據字段名稱）
    const dateFields = ['expectedArrivalDate', 'expiryDate'];
    const dateFieldIndices = headers.map((header, index) => {
      const fieldKey = getFieldKey(header);
      return dateFields.includes(fieldKey) ? index : -1;
    }).filter(index => index !== -1);
    
    const data: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i], delimiter);
      const row: any = { row: i };
      
      // 即使字段數量不匹配也嘗試解析，只是給出警告
      if (values.length !== headers.length) {
        console.warn(`第${i+1}行字段數量不匹配: 期望${headers.length}個字段，實際${values.length}個字段`);
      }
      
      // 按照標題數量進行映射，缺少的字段用空字符串填充
      headers.forEach((header, index) => {
        const fieldKey = getFieldKey(header);
        let value = (index < values.length ? values[index] : '').trim();
        
        // 如果是日期字段，進行格式轉換
        if (dateFieldIndices.includes(index) && value) {
          value = convertExcelDate(value);
        }
        
        row[fieldKey] = value;
      });
      
      data.push(row);
    }

    return data;
  };

  // 解析CSV行 - 正確處理空字段（支持自定義分隔符）
  const parseCSVLine = (line: string, delimiter: string = ','): string[] => {
    // 移除行末的換行符
    line = line.replace(/\r?\n$/, '');
    
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    let i = 0;
    
    while (i <= line.length) {
      const char = i < line.length ? line[i] : delimiter; // 在結尾添加虛擬分隔符來處理最後一個字段
      
      if (char === '"') {
        inQuotes = !inQuotes;
        // 不將引號添加到結果中
      } else if (char === delimiter && !inQuotes) {
        // 遇到分隔符，添加當前字段到結果（即使是空的）
        result.push(current.trim());
        current = '';
      } else if (i < line.length) {
        // 普通字符，添加到當前字段
        current += char;
      }
      
      i++;
    }
    
    return result;
  };

  // 字段映射
  const getFieldKey = (header: string): string => {
    const fieldMap: Record<string, string> = {
      '客戶': 'customer',
      '客戶名稱': 'customerName',
      '倉庫': 'warehouse',
      '倉庫名稱': 'warehouseName',
      'SKU ID': 'productSku',
      '預期數量': 'expectedQuantity',
      '基本單位': 'baseUnit',
      '包裝單位': 'packagingUnit',
      '預期到貨日期': 'expectedArrivalDate',
      '到期日期': 'expiryDate',
      '物流方式': 'logisticsMethod',
      '追蹤號碼': 'trackingNumber',
      '發貨聯繫人': 'shipperContactName',
      '發貨聯繫電話': 'shipperContactPhone',
      '貨物備註': 'cargoNotes'
    };
    
    return fieldMap[header] || header;
  };

  // 驗證解析的數據
  const validateParsedData = (data: any[]): PreviewItem[] => {
    return data.map((row, index) => {
      const errors: ValidationError[] = [];
      let status: 'valid' | 'warning' | 'error' = 'valid';

      // 必填字段驗證
      const requiredFields = [
        { key: 'customerName', name: '客戶名稱' },
        { key: 'productSku', name: 'SKU ID' },
        { key: 'expectedQuantity', name: '預期數量' }
      ];

      requiredFields.forEach(field => {
        if (!row[field.key] || row[field.key].toString().trim() === '') {
          errors.push({
            row: index + 1,
            field: field.key,
            value: row[field.key],
            error: `${field.name}不能為空`,
            severity: 'error'
          });
          status = 'error';
        }
      });

      // 數字字段驗證（可選字段）
      if (row.expectedQuantity && row.expectedQuantity.toString().trim() !== '' && isNaN(Number(row.expectedQuantity))) {
        errors.push({
          row: index + 1,
          field: 'expectedQuantity',
          value: row.expectedQuantity,
          error: '預期數量必須為數字',
          severity: 'error'
        });
        status = 'error';
      }

      // 日期格式驗證（可選字段）
      if (row.expectedArrivalDate && row.expectedArrivalDate.trim() !== '' && !isValidDate(row.expectedArrivalDate)) {
        errors.push({
          row: index + 1,
          field: 'expectedArrivalDate',
          value: row.expectedArrivalDate,
          error: '日期格式不正確，應為YYYY-MM-DD格式',
          severity: 'error'
        });
        status = 'error';
      }

      // 到期日期格式驗證（可選字段）
      if (row.expiryDate && row.expiryDate.trim() !== '' && !isValidDate(row.expiryDate)) {
        errors.push({
          row: index + 1,
          field: 'expiryDate',
          value: row.expiryDate,
          error: '到期日期格式不正確，應為YYYY-MM-DD格式',
          severity: 'error'
        });
        status = 'error';
      }

      return {
        row: index + 1,
        orderNumber: row.orderNumber || '',
        customer: row.customer || '',
        customerName: row.customerName || '',
        warehouse: row.warehouse || '',
        warehouseName: row.warehouseName || '',
        productSku: row.productSku || '',
        expectedQuantity: Number(row.expectedQuantity) || 0,
        baseUnit: row.baseUnit || '',
        packagingUnit: row.packagingUnit || '',
        expectedArrivalDate: row.expectedArrivalDate || '',
        expiryDate: row.expiryDate || '',
        logisticsMethod: row.logisticsMethod || '',
        trackingNumber: row.trackingNumber || '',
        shipperContactName: row.shipperContactName || '',
        shipperContactPhone: row.shipperContactPhone || '',
        cargoNotes: row.cargoNotes || '',
        status,
        errors
      };
    });
  };

  // 驗證日期格式
  const isValidDate = (dateString: string): boolean => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  // 生成驗證錯誤列表
  const generateValidationErrors = (data: PreviewItem[]): ValidationError[] => {
    const errors: ValidationError[] = [];
    data.forEach(item => {
      errors.push(...item.errors);
    });
    return errors;
  };

  // 生成訂單號
  const generateOrderNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `IB${year}${month}${day}${random}`;
  };

  // 確認導入
  const confirmImport = async () => {
    const validData = previewData.filter(item => item.status === 'valid' || item.status === 'warning');
    
    if (validData.length === 0) {
      message.error('沒有有效的數據可以導入');
      return;
    }

    // 【修復】一次導入的所有記錄使用同一個入庫單號（系統自動生成）
    const sharedOrderNumber = generateOrderNumber();
    
    // 【修復】使用第一條記錄的訂單級別字段，確保所有記錄的訂單級別字段一致
    const firstItem = validData[0];
    const sharedOrderLevelFields = {
      customerName: firstItem.customerName,
      warehouseName: firstItem.warehouseName,
      expectedArrivalDate: firstItem.expectedArrivalDate,
      logisticsMethod: firstItem.logisticsMethod,
      trackingNumber: firstItem.trackingNumber,
      shipperContactName: firstItem.shipperContactName,
      shipperContactPhone: firstItem.shipperContactPhone,
      cargoRemark: firstItem.cargoNotes
    };

    // 構建後端需要的格式
    const ordersPayload = validData.map(item => ({
      orderNumber: sharedOrderNumber, // 所有記錄使用同一個訂單號
      customerName: sharedOrderLevelFields.customerName, // 使用第一條記錄的客戶名稱
      warehouseName: sharedOrderLevelFields.warehouseName, // 使用第一條記錄的倉庫名稱
      productSku: item.productSku, // 產品級別字段保持各記錄不同
      status: 'new',
      expectedQuantity: item.expectedQuantity, // 產品級別字段保持各記錄不同
      baseUnit: item.baseUnit, // 產品級別字段保持各記錄不同
      packagingUnit: item.packagingUnit, // 產品級別字段保持各記錄不同
      expectedArrivalDate: sharedOrderLevelFields.expectedArrivalDate, // 使用第一條記錄的預期到貨日期
      expiryDate: item.expiryDate || null, // 產品級別字段保持各記錄不同
      logisticsMethod: sharedOrderLevelFields.logisticsMethod, // 使用第一條記錄的物流方式
      trackingNumber: sharedOrderLevelFields.trackingNumber, // 使用第一條記錄的追蹤號碼
      shipperContactName: sharedOrderLevelFields.shipperContactName, // 使用第一條記錄的發貨聯繫人
      shipperContactPhone: sharedOrderLevelFields.shipperContactPhone, // 使用第一條記錄的發貨聯繫電話
      cargoRemark: sharedOrderLevelFields.cargoRemark // 使用第一條記錄的貨物備註
    }));

    try {
      const resp: any = await apiService.bulkCreateInboundOrders(ordersPayload);
      const created = resp?.data || resp?.created || [];
      
      notification.success({
        message: '批量導入成功',
        description: `成功導入 ${validData.length} 個產品到入庫單 ${sharedOrderNumber}`,
        placement: 'topRight',
      });

      setCurrentStep(2);
    } catch (error) {
      console.error('批量導入失敗:', error);
      message.error('批量導入失敗，請檢查網絡連接或聯繫技術支持');
    }
  };

  // 表格列配置
  const previewColumns: ColumnsType<PreviewItem> = [
    {
      title: '行號',
      dataIndex: 'row',
      width: 60,
      render: (row: number, record: PreviewItem) => (
        <Tag color={record.status === 'error' ? 'red' : record.status === 'warning' ? 'orange' : 'green'}>
          {row}
        </Tag>
      ),
    },
    {
      title: '客戶',
      dataIndex: 'customerName',
      width: 120,
    },
    {
      title: '倉庫',
      dataIndex: 'warehouseName',
      width: 100,
    },
    {
      title: '預期數量',
      dataIndex: 'expectedQuantity',
      width: 80,
    },
    {
      title: '預期到貨日期',
      dataIndex: 'expectedArrivalDate',
      width: 120,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={status === 'error' ? 'red' : status === 'warning' ? 'orange' : 'green'}>
          {status === 'error' ? '錯誤' : status === 'warning' ? '警告' : '有效'}
        </Tag>
      ),
    },
  ];

  const errorColumns: ColumnsType<ValidationError> = [
    {
      title: '行號',
      dataIndex: 'row',
      width: 60,
    },
    {
      title: '字段',
      dataIndex: 'field',
      width: 100,
    },
    {
      title: '值',
      dataIndex: 'value',
      width: 150,
      render: (value: any) => value || <Text type="secondary">空值</Text>,
    },
    {
      title: '錯誤信息',
      dataIndex: 'error',
      render: (error: string, record: ValidationError) => (
        <Text type={record.severity === 'error' ? 'danger' : 'warning'}>
          {error}
        </Text>
      ),
    },
    {
      title: '嚴重性',
      dataIndex: 'severity',
      width: 80,
      render: (severity: string) => (
        <Tag color={severity === 'error' ? 'red' : 'orange'}>
          {severity === 'error' ? '錯誤' : '警告'}
        </Tag>
      ),
    },
  ];

  const steps = [
    {
      title: '上傳文件',
      description: '選擇並上傳CSV文件',
    },
    {
      title: '數據驗證',
      description: '驗證數據格式和內容',
    },
    {
      title: '導入完成',
      description: '成功導入數據',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 頁面標題 */}
      <div style={{ marginBottom: '24px' }}>
        <Space align="center">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/inventory/inbound')}
          >
            返回
          </Button>
          <Title level={2} style={{ margin: 0 }}>批量上傳入庫訂單</Title>
        </Space>
      </div>

      <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />

      {currentStep === 0 && (
        <div>
          <Alert
            message="批量上傳說明"
            description={
              <div>
                <p>現在支持直接上傳CSV/Excel文件，系統將自動解析和驗證數據。您也可以下載模板文件作為參考。</p>
                <p style={{ marginTop: 8, marginBottom: 0 }}>
                  <strong>💡 多產品訂單支持：</strong>相同的「訂單號」會自動合併為一張訂單，每個SKU作為獨立明細項。
                </p>
                <p style={{ marginTop: 8, marginBottom: 0 }}>
                  <strong>✨ 空字段自動繼承：</strong>同一訂單的多個產品行中，訂單級別字段（客戶、倉庫、物流等）可以留空，系統會自動從上一行繼承，無需重複輸入！
                </p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Card title="步驟 1: 下載模板" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                請先下載入庫訂單模板文件，模板包含所有必需的字段和格式說明。支持Excel格式（.xlsx），可直接用Microsoft Excel或WPS編輯。
              </Paragraph>
              <div style={{
                backgroundColor: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '12px'
              }}>
                <Text strong style={{ color: '#0050b3' }}>📦 模板示例說明</Text>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#666' }}>
                  <li><strong>訂單 IN-2025-001</strong>：包含3個產品（SKU001、SKU002、SKU003）→ 演示多產品訂單</li>
                  <li><strong>訂單 IN-2025-002</strong>：包含2個產品（SKU004、SKU005）→ 演示第二張訂單</li>
                  <li><strong>✨ 空字段自動繼承</strong>：第2、3、5行的訂單級別字段留空，自動從上一行繼承</li>
                  <li>下載後可直接上傳測試，或修改為您的實際數據</li>
                </ul>
              </div>
              <div style={{
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <Text strong style={{ color: '#389e0d' }}>✓ 重要提示</Text>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#666' }}>
                  <li>必填字段：<strong>客戶名稱</strong>、<strong>SKU ID</strong>、<strong>預期數量</strong></li>
                  <li>日期格式：YYYY-MM-DD（如：2025-01-15）</li>
                  <li>數量字段：直接輸入數字（如：100）</li>
                  <li>相同訂單號會自動合併為一張訂單</li>
                  <li>Excel文件可直接用Microsoft Excel或WPS編輯，無需擔心編碼問題</li>
                </ul>
              </div>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={downloadTemplate}
                size="large"
              >
                下載Excel模板
              </Button>
            </Space>
          </Card>

          <Card title="步驟 2: 上傳文件">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                填寫完成後，請上傳文件進行數據驗證和預覽。
              </Paragraph>
              <Upload {...uploadProps}>
                <Button 
                  icon={<UploadOutlined />} 
                  size="large"
                  loading={isProcessing}
                >
                  {isProcessing ? '處理中...' : '選擇文件上傳'}
                </Button>
              </Upload>
              {uploadedFile && (
                <div>
                  <Text>已選擇文件: {uploadedFile.name}</Text>
                  {isProcessing && (
                    <Progress percent={uploadProgress} style={{ marginTop: 8 }} />
                  )}
                </div>
              )}
            </Space>
          </Card>

          <Card title="字段說明" style={{ marginTop: 16 }}>
            <Row gutter={16}>
              {fieldDescriptions.map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space>
                    <Tag color={field.required ? 'red' : 'default'}>
                      {field.required ? '必填' : '可選'}
                    </Tag>
                    <Text strong>{field.name}</Text>
                  </Space>
                  <div style={{ fontSize: '12px', color: '#666', marginLeft: 16 }}>
                    {field.description}
                  </div>
                </Col>
              ))}
            </Row>
          </Card>


        </div>
      )}

      {currentStep === 1 && (
        <div>
          <Alert
            message={`數據驗證完成，共 ${previewData.length} 行數據`}
            description={
              <Space>
                <span>有效: {previewData.filter(item => item.status === 'valid').length}</span>
                <span>警告: {previewData.filter(item => item.status === 'warning').length}</span>
                <span>錯誤: {previewData.filter(item => item.status === 'error').length}</span>
                <span style={{ marginLeft: '20px', color: '#666' }}>
                  | 按鈕狀態: {validationErrors.filter(e => e.severity === 'error').length > 0 || 
                             previewData.filter(item => item.status === 'valid' || item.status === 'warning').length === 0 
                             ? '🚫 已禁用' : '✅ 可用'}
                </span>
              </Space>
            }
            type={validationErrors.filter(e => e.severity === 'error').length > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 16 }}
          />

          {validationErrors.length > 0 && (
            <Card 
              title={
                <Space>
                  <span>驗證錯誤</span>
                  <Tag color="red">
                    {validationErrors.filter(e => e.severity === 'error').length} 個錯誤
                  </Tag>
                  <Tag color="orange">
                    {validationErrors.filter(e => e.severity === 'warning').length} 個警告
                  </Tag>
                </Space>
              } 
              style={{ marginBottom: 16 }}
            >
              {validationErrors.filter(e => e.severity === 'error').length > 0 && (
                <Alert
                  message="存在錯誤項目"
                  description="請修正所有錯誤項目後才能進行導入。警告項目不會阻止導入，但建議檢查。"
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              <Table
                dataSource={validationErrors}
                columns={errorColumns}
                rowKey={(record, index) => `${record.row}-${record.field}-${index}`}
                size="small"
                pagination={{ pageSize: 5 }}
              />
            </Card>
          )}

          <Card title="數據預覽" extra={
            <Space>
              <Button onClick={() => setCurrentStep(0)}>重新上傳</Button>
              {(() => {
                const hasErrors = validationErrors.filter(e => e.severity === 'error').length > 0;
                const validDataCount = previewData.filter(item => item.status === 'valid' || item.status === 'warning').length;
                const isDisabled = validDataCount === 0 || hasErrors;

                return (
                  <Tooltip 
                    title={
                      hasErrors
                        ? `存在 ${validationErrors.filter(e => e.severity === 'error').length} 個錯誤，請修正後重新上傳`
                        : validDataCount === 0
                        ? '沒有有效的數據可以導入'
                        : '確認導入數據'
                    }
                  >
                    <Button 
                      type={isDisabled ? 'default' : 'primary'}
                      onClick={isDisabled ? undefined : confirmImport}
                      disabled={isDisabled}
                      style={{
                        backgroundColor: isDisabled ? '#f5f5f5' : undefined,
                        borderColor: isDisabled ? '#d9d9d9' : undefined,
                        color: isDisabled ? 'rgba(0, 0, 0, 0.25)' : undefined,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.6 : 1
                      }}
                    >
                      確認導入
                    </Button>
                  </Tooltip>
                );
              })()}
            </Space>
          }>
            <Table
              dataSource={previewData}
              columns={previewColumns}
              rowKey="row"
              size="small"
              pagination={{ pageSize: 10 }}
              rowClassName={(record) => 
                record.status === 'error' ? 'row-error' : 
                record.status === 'warning' ? 'row-warning' : 'row-valid'
              }
            />
          </Card>
        </div>
      )}

      {currentStep === 2 && (
        <Result
          status="success"
          title="批量導入成功！"
          subTitle={`成功導入 ${previewData.filter(item => item.status === 'valid' || item.status === 'warning').length} 個入庫訂單`}
          icon={<CheckCircleOutlined />}
          extra={[
            <Button type="primary" key="back" onClick={() => navigate('/inventory/inbound')}>
              返回入庫管理
            </Button>,
            <Button key="new" onClick={() => {
              setCurrentStep(0);
              setPreviewData([]);
              setValidationErrors([]);
            }}>
              繼續上傳
            </Button>,
          ]}
        />
      )}

      <style>{`
        .row-error {
          background-color: #fff2f0 !important;
        }
        .row-warning {
          background-color: #fffbe6 !important;
        }
        .row-valid {
          background-color: #f6ffed !important;
        }
        
        /* 强制禁用按钮樣式 */
        .ant-btn-primary[disabled], 
        .ant-btn-primary[disabled]:hover, 
        .ant-btn-primary[disabled]:focus, 
        .ant-btn-primary[disabled]:active {
          background-color: #d9d9d9 !important;
          border-color: #d9d9d9 !important;
          color: #666 !important;
          cursor: not-allowed !important;
          opacity: 0.6 !important;
        }
        
        /* 確保禁用狀態下的樣式覆蓋 */
        .ant-btn[disabled] {
          background-color: #f5f5f5 !important;
          border-color: #d9d9d9 !important;
          color: rgba(0, 0, 0, 0.25) !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
        }
      `}</style>
    </div>
  );
};

export default InboundBulkUploadPage;
